// API Configuration
// Runtime URL-based source detection
// NO caching - always reads from window.location at call time

// =============================================================================
// Constants
// =============================================================================
const STAGING_API_BASE = 'https://staging-live.theexit88.com/api';
const PRODUCTION_API_BASE = 'https://live.theexit88.com/api';

// =============================================================================
// Runtime Helpers - NEVER CACHED
// =============================================================================

/**
 * Get source from URL query parameter - SINGLE SOURCE OF TRUTH
 * Reads from window.location at call time, never cached
 * @returns 'neon' | 'mock'
 */
export function getSourceFromUrl() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  
  try {
    const url = new URL(window.location.href);
    const source = url.searchParams.get('source');
    
    // Only allow explicit 'neon' or 'mock'
    if (source === 'neon' || source === 'mock') {
      return source;
    }
  } catch (e) {
    console.warn('[API Config] Failed to parse URL:', e);
  }
  
  return null; // No explicit source in URL
}

/**
 * Detect environment from hostname - called at runtime
 */
export function detectEnvironment() {
  if (typeof window === 'undefined' || !window.location) {
    return 'server';
  }
  
  const hostname = window.location.hostname.toLowerCase();
  
  if (hostname.startsWith('staging-') || hostname.includes('staging')) {
    return 'staging';
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  return 'production';
}

/**
 * Get API base URL based on hostname - CALLED AT RUNTIME
 */
export function getApiBaseUrl() {
  // Check env var first
  const envApiBase = process.env.REACT_APP_API_BASE_URL;
  if (envApiBase && envApiBase.trim() !== '') {
    return envApiBase;
  }

  // Hostname-based detection
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname.toLowerCase();
    
    if (hostname.startsWith('staging-') || hostname.includes('staging')) {
      return STAGING_API_BASE;
    }
  }

  return PRODUCTION_API_BASE;
}

/**
 * Get data source - RUNTIME EVALUATION
 * Priority:
 * 1. Explicit ?source= parameter in URL
 * 2. Default based on environment (localhost=mock, else=neon)
 */
export function getDataSource() {
  // First: check URL for explicit source
  const urlSource = getSourceFromUrl();
  if (urlSource) {
    return urlSource;
  }
  
  // Second: default based on environment
  const env = detectEnvironment();
  if (env === 'local') {
    return 'mock';
  }
  
  // Production and staging default to neon
  return 'neon';
}

/**
 * Check if we're in Neon mode
 */
export function isNeonMode() {
  return getDataSource() === 'neon';
}

// =============================================================================
// Config Object with Runtime Getters
// =============================================================================

export const config = {
  get apiBaseUrl() {
    return getApiBaseUrl();
  },
  
  get environment() {
    return detectEnvironment();
  },
  
  get dataSource() {
    return getDataSource();
  },
  
  features: {
    auth: process.env.REACT_APP_LIVE_AUTH === 'true',
    events: process.env.REACT_APP_LIVE_EVENTS === 'true',
    artists: process.env.REACT_APP_LIVE_ARTISTS === 'true',
    tickets: process.env.REACT_APP_LIVE_TICKETS === 'true',
    chat: process.env.REACT_APP_LIVE_CHAT === 'true',
    payments: process.env.REACT_APP_LIVE_PAYMENTS === 'true',
  },
};

export const isLive = (feature) => config.features[feature] === true;

// =============================================================================
// Startup Logging - REQUIRED FOR DEBUGGING
// =============================================================================

if (typeof window !== 'undefined' && window.location) {
  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    const source = getDataSource();
    const urlSource = getSourceFromUrl();
    const env = detectEnvironment();
    const apiBase = getApiBaseUrl();
    
    console.log('='.repeat(60));
    console.log('[API Config] STARTUP');
    console.log('='.repeat(60));
    console.log('[API Config] URL:', window.location.href);
    console.log('[API Config] URL Source Param:', urlSource || '(not set)');
    console.log('[API Config] Environment:', env);
    console.log('[API Config] API Base URL:', apiBase);
    console.log('[DataSource] source=' + source);
    console.log('='.repeat(60));
  });
}

export default config;
